import {Component, OnInit } from '@angular/core';
import { SubscriptionService } from '../../subscription.service';
import { Table, Pagination, TableActions, TableAction, TableData, TableDataTypes } from '../../../shared/types';
import { ConfirmDialogService } from '../../../shared/confirm-dialog/confirm-dialog.service';
import { SharedService } from '../../../shared/shared.service';
import { ToasterService } from '../../../toaster.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../auth.service';
import { Toaster, ToasterType } from '../../../shared/types/toaster.types';
import { LoaderService } from '../../../loader.service';
import { AlertDialogOptions, AlertDialogService } from '../../../shared/alert-dialog/alert-dialog.service';
import { subscriptionOperationFailureAlert, subscriptionUpdateSuccessAlert } from '../../subscription.create-alert';
import { AuthzAccessHintService } from '../../../shared/authz-access-hint/authz-access-hint.service';
import { AUTHZ_FEATURES } from '../../../constants/permissions';
import { permissionsForFeature } from '../../../utils/permission.utils';

@Component({
  selector:'app-list-accessfee',
  templateUrl: './accessFee.list.component.html',
  providers: [SubscriptionService]
})
export class ListAccessFeeComponent implements OnInit{
  public table: Table = new Table({
    headers:{
      status: 'Status',
      cities: 'City',
      zoneNames: 'Zone',
      planName: 'Name',
      serviceNames:'Service',
      startDate: 'Start Date',
      endDate: 'End Date',
      userSelector: "User Selector",
      priority: "Priority",
      action: "Actions"
    },
    pagination: new Pagination(0, 15, 150, [15, 20]),
    data: []
  });
  public showFilters = true;
  public authzAccessHint: { message: string; requestUrl: string } | null = null;

  public requestBody = {
    filters: {} as any,
    sort: {
      sortBy: "startDate",
      sortOrder: -1
    }
  };

  private cities = [];
  private services = {};
  private serviceIdToName: { [id: string]: string } = {};
  public cityMap = {};
  public cityIdNameMap = {};
  public zoneIdNameMap = {};
  public userSelectors: { id: string, name: string; }[] = [];
  public serviceMap = {};
  private citiesFilter = {
    type: 'searchable-dropdown',
    title: 'City',
    values: [],
    default: [],
    key: 'accessfee-city'
  }

  private serviceFilter = {
    type: 'searchable-dropdown',
    title: 'Services',
    values: [],
    default: [],
    key: 'accessfee-services'
  }

  public filterComponents: any[] = [
    this.citiesFilter,
    this.serviceFilter,
    {
      type: 'calendar',
      title: '',
      values: [],
      default: undefined,
      key: 'accessfee-dates',
    },
    {
      type: 'radio-button',
      title: 'Status',
      name:'status',
      values: ['Both', 'Active', 'Inactive'],
      default: 'Both',
      key: 'accessfee-status'
    }
  ];
  constructor(
    private subscriptionService: SubscriptionService,
    private sharedService: SharedService,
    private loaderService: LoaderService,
    private router: Router,
    private route: ActivatedRoute,
    private accessRights: AuthService,
    public toastService: ToasterService,
    private dialogService: ConfirmDialogService,
    private alertDialogService: AlertDialogService,
    private authzAccessHintService: AuthzAccessHintService,
  ) {
  }
  async ngOnInit() {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData('billingPlans');
    await this.fetchAllCities();
    await this.applyLaunchQueryParamsToFilters();
  }

  private async applyLaunchQueryParamsToFilters(): Promise<boolean> {
    const q = this.route.snapshot.queryParamMap;
    const cityId = (q.get('city') || '').trim();
    const serviceId = (q.get('service') || '').trim();
    if (!cityId) {
      return false;
    }
    const city = this.cities.find((c: any) => String(c._id) === String(cityId));
    if (!city) {
      return false;
    }
    const cityDisplayName = city.displayName;
    await this.withLoader(() => this.fetchServices([cityDisplayName]));

    this.patchFilterComponent('accessfee-city', {
      initialSelected: [cityDisplayName],
      default: [cityDisplayName]
    });

    const launchFilters: any = {
      'accessfee-city': [cityDisplayName],
      'accessfee-services': []
    };

    if (serviceId) {
      const serviceName = this.serviceIdToName[String(serviceId)] || '';
      if (serviceName && this.services[serviceName]) {
        this.patchFilterComponent('accessfee-services', {
          initialSelected: [serviceName],
          default: [serviceName]
        });
        launchFilters['accessfee-services'] = [serviceName];
      } else {
        this.toastService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: 'Service from launch URL is not available for this city.'
        }));
      }
    }

    this.refreshFilterComponents();
    this.showFilters = true;

    if (launchFilters['accessfee-services'].length) {
      this.applyFilters(launchFilters);
    }
    return true;
  }

  private patchFilterComponent(key: string, patch: any) {
    this.filterComponents = this.filterComponents.map(node =>
      (node && node.key === key ? { ...node, ...patch } : node));
  }

  private refreshFilterComponents() {
    this.filterComponents = this.filterComponents.map(node => ({ ...node }));
  }

  async withLoader(cb) {
    try {
      this.loaderService.openLoading();
      return await cb();
    } finally {
      this.loaderService.closeLoading()
    }
  }

  async fetchAllCities() {
    const citiesRes: any = await this.withLoader(() => this.sharedService.fetchCities().toPromise());
    const citiesData =citiesRes.data.cities;

    this.cities = citiesData;
    this.citiesFilter.values = citiesData.map(city => city.displayName);

      for (const city of citiesData) {
          this.cityMap[city.displayName] = city._id;
          this.cityIdNameMap[city._id] = city.displayName;
      }
  }

  async fetchServices(cities) {
    this.services = {};
    this.serviceIdToName = {};
    const cityMap = new Map();
    for (const city of this.cities) {
        cityMap.set(city.displayName, city._id);
    }

    for (const city of cities) {
      const cityId = cityMap.get(city);
      if (cityId !== undefined) {
        const res: any = await this.sharedService.fetchServices(cityId).toPromise()

        for (const serviceDetail of res.data) {
          if(serviceDetail.service) {
            const name = serviceDetail.service.name;
            this.services[name] = this.services[name] || [];
            this.services[name].push(serviceDetail.service._id);
            this.serviceMap[serviceDetail.service._id] = serviceDetail._id;
            this.serviceIdToName[String(serviceDetail.service._id)] = name;
          }
        }
      }
    }
    this.serviceFilter.values = Object.keys(this.services)
  }

  async filterChange(filters) {
    if (filters['accessfee-city'] && filters['accessfee-city'].length) {
      this.withLoader(() => this.fetchServices(filters['accessfee-city']));
    }
  }
  async sortByHeader(header) {
    const sortOrder = header.includes("-") ? -1 : 1;
    const column = header.replace("-", "");
    if (!["startDate"].includes(column)) {
      return;
    }

    this.requestBody.sort.sortBy = column;
    this.requestBody.sort.sortOrder = sortOrder;

    await this.fetchTableData()
  }

  applyFilters(filters) {
    this.table.pagination.setPageNoToZero();
    const filtersRequest: any = {};

    if (filters['accessfee-status'] === 'Active') {
      filtersRequest.active = true;
    }

    if (filters['accessfee-status'] === 'Inactive') {
      filtersRequest.active = false;
    }

    const [startDate = undefined, endDate = undefined] = filters['accessfee-dates'] || [];

    filtersRequest.startDate = startDate  
    filtersRequest.endDate = endDate 

    let allServiceIds = [];
    for (const service of (filters['accessfee-services'] || [])) {
      allServiceIds = allServiceIds.concat(this.services[service]);
    }
    if (allServiceIds.length > 0) {
      filtersRequest['services'] = allServiceIds;
    }
    if ((filters['accessfee-city'] || []).length > 0) {
      filtersRequest['cities'] = filters['accessfee-city'].map(city=>this.cityMap[city]);
    }

    this.requestBody.filters = filtersRequest;
    this.fetchTableData();
  }

  async fetchTableData() {
    await this.withLoader(() => this.fetchBillingRules());
  }

  async fetchBillingRules() {
    this.validateFilters();
    const payload = {
      filters: this.requestBody.filters,
      pagination: {
        page: this.table.pagination.pageNo + 1,
        pageSize: this.table.pagination.perPage,
      },
      queryParams: {
        sortBy: this.requestBody.sort.sortBy,
        sortOrder: this.requestBody.sort.sortOrder,
      }
    };

    const editPermission = (permissionsForFeature(AUTHZ_FEATURES.BILLING_PLANS, this.accessRights.getAuthzPanels()) || {}).edit;
    const canDeactivate = this.accessRights.isAuthorizedFor(editPermission, AUTHZ_FEATURES.BILLING_PLANS);
    const rules: any = await this.subscriptionService.getBillingPlans(payload).toPromise()
    this.table.data = rules
      .map(rule => {  
        const serviceNames = rule.serviceNames || [];
        const userSelectors = rule.userSelectors || [];
        const zoneNames = rule.zoneNames || [];
        const row ={
          id: rule.id,
          planName: rule.planName,
          cities: this.cityIdNameMap[rule.city],
          serviceNames: serviceNames.join(', '),
          zoneNames: zoneNames.join(', '),
          userSelector : userSelectors.length > 0 ? "Yes" : "No",
          status: rule.active ? 'Active' : 'Inactive',
          startDate: rule.startDate || "--",
          endDate: rule.endDate || "--",
          priority: rule.priority || "--",
          action: this.createAction(canDeactivate, rule)
        };
        return row;

      });
  }
  validateFilters() {
    this.validate("cities", "Select city and services to fetch accessfee plans");
    this.validate("services", "Select services to fetch accessfee plans");
  }

  validate(key: string, message: string) {
    if (!this.requestBody.filters[key]) {
      this.showFilters = true;
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: message
      }))
      throw new Error(message);
    }
  }

  createAction(canDeactivate: boolean, rule: any) {
    const actionArr = [];

    actionArr.push(
      new TableAction({

        onClick: (data) => {
          this.openPage(`/accessfee/view/${data.id}`)
        },
        text: 'View'
      })
    );

    if (canDeactivate && rule.active) {
      actionArr.push(
        new TableAction({

          onClick: async (data) => {
            await this.deactivateAccessFeeRule(data);
          },
          text: 'Deactivate'
        })
      );
    }
    return new TableData({
      actions: new TableActions(actionArr),
      type: TableDataTypes.ACTION
    });
  }
  private showAlertBox(options: AlertDialogOptions, cb = () => { }) {
    this.alertDialogService.open(options);
    this.alertDialogService.confirmed()
      .subscribe(confirmed => confirmed && cb());
  }

  async deactivateAccessFeeRule(planData:any){
      const options = {
        title: `Are you sure to deactivate the ${planData.planName} plan ?`,
        message: `You are about to deactivate accessfee ${planData.planName}`,
        cancelText: 'Cancel',
        confirmText: 'Yes, Proceed'
      };

      this.dialogService.open(options);
      this.dialogService.confirmed().subscribe(async (confirmed) => {
        if (confirmed) {
          const payload = {
            active:false
          }
          try{
            const resp = await this.subscriptionService.updateBillingPlan(planData.id, payload).toPromise();
            this.showAlertBox(subscriptionUpdateSuccessAlert(resp));
          } catch (error) {
            this.showAlertBox(subscriptionOperationFailureAlert(error));
          }
        }
    })
  }
  private openPage(url) {
    window.open(this.router.serializeUrl(this.router.createUrlTree([url])))
  }
}